import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { navigateInApp } from 'lib/navigation';
import { useGameSocket } from 'hooks/useGameSocket';
import { useTableLayoutMode } from 'hooks/useTableLayoutMode';
import { useTableExitNavigation } from 'hooks/useTableExitNavigation';
import { syncTableViewportHeight } from 'hooks/useFullscreen';
import GameTable from 'components/GameTable';
import { PlayerAction, API_ENDPOINTS } from 'constants/game';
import { clearPendingRoomCode } from 'lib/pendingRoomCode';
import { formatVariantLabel } from 'lib/format-variant';
import { useAuth } from 'context/AuthProvider';
import api from 'lib/api';
import { PrivateRoomInfo } from 'types/game';
import { buyInBlockMessage } from 'lib/buy-in-messages';
import { privateRoomToBuyInInfo } from 'lib/private-room-buy-in';
import { privateRoomJoinErrorMessage } from 'lib/private-room-messages';

function privateLobbyPath(variantCode?: string | null): string {
  const variant = formatVariantLabel(variantCode || 'Classic');
  return `/lobby?variant=${encodeURIComponent(variant)}&private=1`;
}

export default function PrivateTablePage() {
  const { roomCode: roomCodeParam } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const pageRef = useRef<HTMLDivElement>(null);
  const roomCode = (roomCodeParam || '').trim().toUpperCase();

  const [joinReady, setJoinReady] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [variantCode, setVariantCode] = useState<string | undefined>();

  const {
    isFullscreen,
    isImmersiveActive,
    nativeFsChromeHint,
    exitFullscreen,
  } = useTableLayoutMode();

  useEffect(() => {
    clearPendingRoomCode();
  }, []);

  useEffect(() => {
    if (joinReady || !roomCode) return;

    let cancelled = false;
    const prepareJoin = async () => {
      setJoinLoading(true);
      setJoinError('');
      try {
        await refreshSession();
        const res = await api.get<PrivateRoomInfo>(API_ENDPOINTS.FRONTEND.PRIVATE_ROOM(roomCode));
        if (cancelled) return;
        if (!res.success || !res.data) {
          setJoinError(privateRoomJoinErrorMessage(res.message));
          return;
        }
        const info = privateRoomToBuyInInfo(res.data);
        if (!res.data.canJoin) {
          setJoinError(buyInBlockMessage(info));
          return;
        }
        setVariantCode(res.data.variantCode);
        setJoinReady(true);
      } catch {
        if (!cancelled) setJoinError('Could not join room — try again');
      } finally {
        if (!cancelled) setJoinLoading(false);
      }
    };

    void prepareJoin();
    return () => {
      cancelled = true;
    };
  }, [joinReady, roomCode, refreshSession]);

  const effectiveRoomCode = joinReady ? roomCode : undefined;

  const {
    connected,
    tableState,
    myCards,
    battleResult,
    sideShowResult,
    error,
    chatMessages,
    roomClosed,
    turnSeconds,
    turnMaxSeconds,
    handCountdownSeconds,
    handCountdownMax,
    setHandCountdownPaused,
    leaveTable,
    sendAction,
    sendChatMessage,
    clearBattleResult,
    clearError,
  } = useGameSocket(undefined, undefined, effectiveRoomCode);

  useEffect(() => {
    if (roomClosed) {
      const variant = tableState?.variantCode;
      navigateInApp(navigate, privateLobbyPath(variant), { replace: true });
    }
  }, [roomClosed, navigate, tableState?.variantCode]);

  const tableStateRef = useRef(tableState);
  tableStateRef.current = tableState;

  const { handleBack, handleLeave, handleOpenWallet } = useTableExitNavigation({
    fallbackPath: privateLobbyPath(tableState?.variantCode ?? variantCode),
    leaveTable,
    getBattleUuid: () => tableStateRef.current?.battleUuid,
    routePrefix: '/private/',
  });

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      void exitFullscreen();
    };
  }, [exitFullscreen]);

  useEffect(() => {
    const syncViewport = () => {
      syncTableViewportHeight();
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('scroll', syncViewport);
    return () => {
      window.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('scroll', syncViewport);
    };
  }, []);

  const handleAction = (action: PlayerAction, amount?: number) => {
    if (!tableState) return;
    sendAction(tableState.battleUuid, action, amount);
  };

  const pageClassName = [
    'tp-desktop-page fixed inset-0 z-50 flex flex-col overflow-hidden bg-tp-bg',
    isImmersiveActive ? 'tp-table-immersive' : '',
    isFullscreen ? 'tp-native-fullscreen' : '',
    nativeFsChromeHint ? 'tp-native-fs-chrome-hint' : '',
    'tp-table-portrait-mode',
  ]
    .filter(Boolean)
    .join(' ');

  const chatHandlers = {
    onSendChatText: (text: string) => {
      if (!tableState) return;
      sendChatMessage(tableState.battleUuid, { kind: 'text', text });
    },
    onSendChatPhrase: (phraseId: string) => {
      if (!tableState) return;
      sendChatMessage(tableState.battleUuid, { kind: 'phrase', phraseId });
    },
    onSendChatSticker: (stickerId: string) => {
      if (!tableState) return;
      sendChatMessage(tableState.battleUuid, { kind: 'sticker', stickerId });
    },
  };

  const waitingLabel = joinLoading
    ? 'Joining room…'
    : joinError
      ? joinError
      : joinReady
        ? `Connecting to private room ${roomCode}…`
        : 'Preparing private room…';

  return (
    <div
      ref={pageRef}
      className={pageClassName}
      style={{ height: 'calc(var(--tp-vh, 1dvh) * 100)', maxHeight: 'calc(var(--tp-vh, 1dvh) * 100)' }}
    >
      {!connected && joinReady && (
        <div className="absolute left-1/2 top-16 z-[60] -translate-x-1/2 rounded-xl border border-tp-gold/30 bg-black/70 px-5 py-2 text-sm text-tp-gold backdrop-blur-md">
          Connecting to game server…
        </div>
      )}
      {error && joinReady && (
        <div className="absolute left-1/2 top-16 z-[60] flex w-[min(90%,420px)] -translate-x-1/2 items-center justify-between rounded-xl bg-red-950/90 px-4 py-3 text-sm ring-1 ring-red-500/40">
          <span>{error}</span>
          <button type="button" onClick={clearError} className="text-white/70 underline">
            Dismiss
          </button>
        </div>
      )}
      {!tableState ? (
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="text-center">
            <div className="mx-auto h-14 w-14 animate-spin rounded-full border-2 border-tp-gold border-t-transparent" />
            <p className={`mt-4 text-sm ${joinError ? 'text-red-200' : 'text-white/50'}`}>{waitingLabel}</p>
            {joinError ? (
              <button
                type="button"
                className="mt-4 text-sm font-semibold text-tp-gold hover:underline"
                onClick={() => navigateInApp(navigate, privateLobbyPath(variantCode), { replace: true })}
              >
                Back to lobby
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <GameTable
            state={tableState}
            myCards={myCards}
            turnSeconds={turnSeconds}
            turnMaxSeconds={turnMaxSeconds}
            handCountdownSeconds={handCountdownSeconds}
            handCountdownMax={handCountdownMax}
            setHandCountdownPaused={setHandCountdownPaused}
            battleResult={battleResult}
            sideShowResult={sideShowResult}
            onClearBattleResult={clearBattleResult}
            onAction={handleAction}
            onBack={handleBack}
            onLeave={handleLeave}
            onOpenWallet={handleOpenWallet}
            chatMessages={chatMessages}
            fallbackRoomCode={roomCode}
            {...chatHandlers}
          />
        </div>
      )}
    </div>
  );
}
